/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */
import { useTranslate } from '@im/react-localization';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import React, { useState, useEffect } from 'react';
import Chip from '@mui/material/Chip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Pagination } from '@im/ui-lib/Pagination';
import NoResultsScreen from './NoResultsScreen';
import FormControl from '@mui/material/FormControl';
import AdvancedSearchSetting from '../advanceSearch/AdvancedSearchSetting';
import { SearchSettings } from '../advanceSearch/InterfaceTypes';
import { useLocation, useNavigate } from 'react-router';
import {
  useFilterSettings,
  useFiltersItems,
  requestGenerator,
  useAdvancedSearchV2Query,
  useAdvancedSearchV2QueryBusinessTypes,
  useDealAdvanceSearchDownloadFile,
  AdvancesdSearchResquest,
} from '../hooks/advanced-search';
import { useSearchParams } from 'react-router-dom';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { StyledText } from '../../../components/StyledTypography';
import SearchResultRow from './SearchResultRow';
import DealSearchBar from '../../../components/DealSearchBar/DealSearchBar';
import { ChipLabel } from './ChipLabel';
import { DownloadButton } from 'src/components/Download/DownloadButton';

export const onChipDeleteGetNewValue = (
  index: number,
  key: string,
  searchSettings: SearchSettings
) => searchSettings[key]?.filter((f, i) => i !== index);

export default function SearchResultScreen() {
  const translate = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const filtersItems = useFiltersItems();
  const filterSettings = useFilterSettings();
  const state = location.state;
  const [isOpenAdvancedSearchModal, setIsOpenAdvancedSearchModal] = useState<
    'NEW' | 'EDIT' | false
  >(false);
  const [searchType, setSearchType] =
    useState<AdvancesdSearchResquest['searchType']>('Simple');
  const [searchSettings, setSearchSettings] = useState<SearchSettings>({});
  const [pageData, setPageData] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });
  const [searchParams, setSearchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');
  const dealNumber = searchParams.get('dealNumber');
  const businessType = searchParams.get('businessType');
  const resultText = dealNumber ?? 'All deals';
  const { data, isError, isLoading } = (
    searchType === 'Advanced'
      ? useAdvancedSearchV2Query
      : useAdvancedSearchV2QueryBusinessTypes
  )(
    requestGenerator({
      businessType: typeof businessType === 'string' ? businessType : '',
      countryCode,
      dealNumber,
      pageRequest: pageData,
      searchSettings,
      searchType,
    })
  );

  const handleAdvancedSearchModal = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setIsOpenAdvancedSearchModal('NEW');
  };
  const closeAdvancedSearchModal = () => {
    setIsOpenAdvancedSearchModal(false);
  };
  const onSearchClick = (searchSettings: SearchSettings) => {
    if (dealNumber) {
      searchParams.delete('dealNumber');
      setSearchParams(searchParams);
    }
    setSearchType('Advanced');
    setSearchSettings(searchSettings);
    setIsOpenAdvancedSearchModal(false);
    navigate(
      `/vendor-program/search-results?countryCode=${countryCode}&companyCode=${companyCode}`,
      {
        state: { searchSettings },
      }
    );
  };

  const onClearSearchClick = () => {
    setSearchType('Advanced');
    setSearchSettings({});
    navigate('.', {
      state: { searchSettings: {} },
    });
  };

  const onChipDelete = (key: string, index: number) => (event: any) => {
    let _searchSettings: SearchSettings = {};

    setSearchSettings((searchSettings) => {
      const newValue = onChipDeleteGetNewValue(index, key, searchSettings);

      if (newValue.length === 0) {
        const { [key]: _, ...rest } = searchSettings;
        _searchSettings = rest;
        return rest;
      }
      const result = {
        ...searchSettings,
        [key]: newValue,
      };
      _searchSettings = result;

      return result;
    });
    setSearchType(
      Object.keys(_searchSettings).length === 0 ? 'Simple' : 'Advanced'
    );
    navigate('.', {
      state: { searchSettings: _searchSettings },
    });
  };
  useEffect(() => {
    if (state) {
      setSearchSettings(state?.searchSettings as SearchSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const downloadDetails = useDealAdvanceSearchDownloadFile(
    requestGenerator({
      businessType: typeof businessType === 'string' ? businessType : '',
      countryCode: String(countryCode),
      dealNumber,
      pageRequest: pageData,
      searchSettings,
      searchType,
    })
  );

  return (
    <>
      {isLoading ? (
        <CenterLoader />
      ) : (
        <Box mt={2} p={2}>
          <Stack direction="row" display="flex">
            <Stack
              alignContent="center"
              direction="row"
              flexWrap="wrap"
              gap={2}
              justifyContent="flex-start"
              width="50%"
            >
              <Typography
                data-testid="gds-vp-searchResult-result"
                sx={(theme) => ({
                  fontSize: theme.typography.pxToRem(20),
                  fontWeight: theme.typography.fontWeightSemiBold,
                  lineHeight: theme.typography.pxToRem(28),
                })}
              >
                {!isError &&
                  translate('VendorDeals.SearchResults', {
                    search:
                      Object.keys(searchSettings).length > 0
                        ? translate('VendorProgram.AdvancedSearch')
                        : resultText,
                  })}
                {isError && translate('VendorDeals.SearchDeals')}
              </Typography>
              {!isError && (
                <StyledText
                  data-testid="gds-vp-searchResult-dealFounds"
                  sx={{ alignContent: 'end', display: 'grid' }}
                >
                  {translate('VendorDeals.foundCount', {
                    count: data?.totalCount,
                  })}
                </StyledText>
              )}
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              width="50%"
            >
              <FormControl sx={{ minWidth: 200, width: '240px' }}>
                <DealSearchBar />
              </FormControl>
              {businessType !== 'Subscription' && (
                <Button
                  data-testid="gds-vp-searchResult-advanced-btn"
                  onClick={handleAdvancedSearchModal}
                  variant="newOutlined"
                >
                  {translate('VendorProgram.AdvancedSearch')}
                </Button>
              )}
              {businessType !== 'Subscription' && (
                <DownloadButton
                  fileName="DealAdvanceSearch.xlsx"
                  mutateAsync={downloadDetails.mutateAsync}
                />
              )}
            </Stack>
          </Stack>
          {!isError && Object.keys(searchSettings).length > 0 && (
            <Stack
              mt={2}
              sx={(theme) => ({
                background: theme.palette.primary[50],
                borderRadius: `${theme.shape.borderRadius}px`,
                gap: theme.spacing(1.5),
                padding: `${theme.spacing(2)} ${theme.spacing(
                  2
                )} ${theme.spacing(3)}`,
              })}
            >
              <Stack direction="row" display="flex">
                <Stack
                  alignContent="center"
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  justifyContent="flex-start"
                  width="50%"
                >
                  <StyledText fontWeight={600}>
                    {translate('VendorProgram.AdvancedSearch')}
                  </StyledText>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={2}
                  width="50%"
                >
                  <Button
                    data-testid="gds-vp-searchResult-clearSearch-btn"
                    onClick={onClearSearchClick}
                    startIcon={<DeleteOutlineIcon />}
                    sx={(theme) => ({ background: theme.palette.common.white })}
                    variant="newOutlined"
                  >
                    {translate('VendorDeals.ClearSearch')}
                  </Button>
                  <Button
                    data-testid="gds-vp-searchResult-editSearch-btn"
                    onClick={(event) => setIsOpenAdvancedSearchModal('EDIT')}
                    startIcon={<EditOutlinedIcon />}
                    sx={(theme) => ({ background: theme.palette.common.white })}
                    variant="newOutlined"
                  >
                    {translate('VendorDeals.EditSearch')}
                  </Button>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1}>
                {Object.keys(searchSettings).map((key) =>
                  searchSettings[key].map((v, i) => (
                    <Chip
                      data-testid={`gds-vp-searchResult-chip-${i}`}
                      key={key}
                      label={<ChipLabel inputValues={v} />}
                      onDelete={onChipDelete(key, i)}
                    />
                  ))
                )}
              </Stack>
            </Stack>
          )}
          {!isError && (
            <Box mt={2}>
              {data?.dealsV2?.map((deal) => (
                <SearchResultRow deal={deal} key={deal.deal_number} />
              ))}
              <Pagination
                data-testid="gds-search-result-pagination"
                onPageChange={(page) => setPageData({ ...pageData, page })}
                onRowsPerPageChange={(pageSize) =>
                  setPageData({ page: 1, pageSize })
                }
                page={pageData.page}
                rowsPerPage={pageData.pageSize}
                totalCount={data?.totalCount ?? 0}
              />
            </Box>
          )}
        </Box>
      )}
      {isError && <NoResultsScreen />}
      <AdvancedSearchSetting
        filterSettings={filterSettings}
        filtersItems={filtersItems}
        handleClose={closeAdvancedSearchModal}
        onSearch={onSearchClick}
        open={Boolean(isOpenAdvancedSearchModal)}
        searchSettings={
          isOpenAdvancedSearchModal === 'EDIT' ? searchSettings : undefined
        }
      />
    </>
  );
}
